// @flow

import { useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import type { Shift, Note, User, SignOutActionCreator, ClockInActionCreator } from '../../typings';
import AnimatedRow from '../Forms/AnimatedRow';
import ClickableRow from '../Forms/ClickableRow';
import ButtonContainer from '../Forms/ButtonContainer';
import RowIcon from '../Forms/RowIcon';
import RowContent from '../Forms/RowContent';
import FormContainer from '../Forms/FormContainer';
import FormScreenContainer from '../ui/FormScreenContainer';
import FormFieldsContainer from '../ui/FormFieldsContainer';
import PaddedContainer from '../ui/PaddedContainer';
import Label from '../Text/Label';
import ContentText from '../Text/ContentText';
import Button from '../Button';
import useTaskViewRenderers from '../../../shiftTasks/helpers/useTaskViewRenderers';
import { SMALL_DEVICE_HEIGHT } from '../../../constants';
import timeIcon from '../../../assets/svg/icons/time-gray.svg';
import locationIcon from '../../../assets/svg/icons/location-gray.svg';
import positionIcon from '../../../assets/svg/icons/position-gray.svg';
import tagIcon from '../../../assets/svg/icons/tag-gray.svg';
import shiftIcon from '../../../assets/svg/icons/shift-gray.svg';
import dateIcon from '../../../assets/svg/icons/date-gray.svg';
import taskIcon from '../../../assets/svg/icons/task-gray.svg';
import arrowRightIcon from '../../../assets/svg/icons/arrow-right.svg';
import TimeClockNote from '../../../timeClock/components/TimeClockNote';

const PaddedAnimatedRow = styled(AnimatedRow)`
  padding-left: 16px;
  width: 100%;
`;

const RowColumn = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const ArrowContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

type Props = {
  shift: Shift,
  notes: Array<Note> | null,
  usTimeFormat: boolean,
  deviceHeight: number,
  user: User | null,
  isIntegrationKiosk: boolean,
  signOut: SignOutActionCreator,
  clockIn: ClockInActionCreator
};

export default function SingleShiftFields({
  shift,
  notes,
  signOut,
  clockIn,
  deviceHeight,
  user,
  isIntegrationKiosk
}: Props) {
  const { location, position, tags, summary, displayLabel, breakLabel, taskInstances = [] } = shift;
  const ref = useRef();
  const { viewTasks, shortenAnimations, render } = useTaskViewRenderers({
    shift,
    user,
    deviceHeight,
    renderProp: renderShift,
    baseScreenRef: ref
  });

  const {
    timeAnimationDelay,
    locationAnimationDelay,
    positionAnimationDelay,
    tagsAnimationDelay,
    tasksAnimationDelay,
    notesAnimationDelay,
    todaysNotesAnimationDelay,
    timeClockNotesAnimationDelay,
    buttonsAnimationDelay
  } = getAnimationDelays(shift, notes, shortenAnimations);

  const handleClockIn = () => {
    if (shift) {
      clockIn(shift.id);
    }
  };

  function renderShift() {
    return (
      <FormScreenContainer ref={ref}>
        <FormContainer>
          <FormFieldsContainer deviceHeight={deviceHeight}>
            <PaddedContainer>
              <AnimatedRow borderTop borderBottom delay={timeAnimationDelay}>
                <RowIcon src={timeIcon} />
                <RowContent>
                  <Label>Time</Label>
                  <ContentText>
                    {displayLabel}
                    {breakLabel ? ` • ${breakLabel}` : ''}
                  </ContentText>
                </RowContent>
              </AnimatedRow>

              {!!location && (
                <AnimatedRow borderBottom delay={locationAnimationDelay}>
                  <RowIcon src={locationIcon} />
                  <RowContent>
                    <Label>Location</Label>
                    <ContentText>{location.name}</ContentText>
                  </RowContent>
                </AnimatedRow>
              )}

              {!!position && (
                <AnimatedRow borderBottom delay={positionAnimationDelay}>
                  <RowIcon src={positionIcon} />
                  <RowContent>
                    <Label>Position</Label>
                    <ContentText color={position.color}>{position.name}</ContentText>
                  </RowContent>
                </AnimatedRow>
              )}

              {!!tags.length && (
                <AnimatedRow borderBottom delay={tagsAnimationDelay}>
                  <RowIcon src={tagIcon} />
                  <RowContent>
                    <Label>Tags</Label>
                    <ContentText>{tags.map(tag => tag.name).join(' • ')}</ContentText>
                  </RowContent>
                </AnimatedRow>
              )}

              {Boolean(taskInstances && taskInstances.length) && (
                <ClickableRow onClick={viewTasks} delay={tasksAnimationDelay}>
                  <PaddedAnimatedRow borderBottom noPaddingHorizontal delay={tasksAnimationDelay}>
                    <RowIcon src={taskIcon} />
                    <RowContent>
                      <ArrowContainer>
                        <RowColumn>
                          <Label>Tasks</Label>
                          <ContentText>
                            {taskInstances?.map(taskInstance => taskInstance.title).join(' • ')}
                          </ContentText>
                        </RowColumn>
                        <img src={arrowRightIcon} alt="Preview task" />
                      </ArrowContainer>
                    </RowContent>
                  </PaddedAnimatedRow>
                </ClickableRow>
              )}

              {!!summary && (
                <AnimatedRow borderBottom delay={notesAnimationDelay}>
                  <RowIcon src={shiftIcon} />
                  <RowContent>
                    <Label>Shift notes</Label>
                    <ContentText>{summary}</ContentText>
                  </RowContent>
                </AnimatedRow>
              )}

              {Boolean(notes && notes.length) && (
                <AnimatedRow borderBottom delay={todaysNotesAnimationDelay}>
                  <RowIcon src={dateIcon} />
                  <RowContent>
                    <Label>Todays notes</Label>
                    {((notes: any): Array<Note>).map(note => (
                      <ContentText key={note.id}>{note.content}</ContentText>
                    ))}
                  </RowContent>
                </AnimatedRow>
              )}

              <TimeClockNote delay={timeClockNotesAnimationDelay} shift={shift} />

              {deviceHeight <= SMALL_DEVICE_HEIGHT &&
                renderButtons(
                  buttonsAnimationDelay,
                  handleClockIn,
                  shift,
                  signOut,
                  isIntegrationKiosk
                )}
            </PaddedContainer>
          </FormFieldsContainer>
          {deviceHeight > SMALL_DEVICE_HEIGHT &&
            renderButtons(buttonsAnimationDelay, handleClockIn, shift, signOut, isIntegrationKiosk)}
        </FormContainer>
      </FormScreenContainer>
    );
  }

  return render();
}

function renderButtons(buttonsAnimationDelay, handleClockIn, shift, signOut, isIntegrationKiosk) {
  return (
    <ButtonContainer delay={buttonsAnimationDelay}>
      <Button
        label="Clock in"
        buttonTheme="grey"
        themeColor="btnGreenTextColor"
        onPress={handleClockIn}
      />

      {!isIntegrationKiosk && (
        <Button label="Cancel" buttonTheme="grey" onPress={signOut} withMargin />
      )}
    </ButtonContainer>
  );
}

function getAnimationDelays(shift: Shift, notes: Array<Note> | null, shortenAnimations: boolean) {
  const { location, position, tags, taskInstances, summary } = shift;

  const timeAnimationDelay = shortenAnimations ? 0 : 1;
  let lastAnimationDelay = shortenAnimations ? 0 : 1;

  let locationAnimationDelay;
  if (location) {
    lastAnimationDelay = locationAnimationDelay = lastAnimationDelay + 0.2;
  }

  let positionAnimationDelay;
  if (position) {
    lastAnimationDelay = positionAnimationDelay = lastAnimationDelay + 0.2;
  }

  let tagsAnimationDelay;
  if (tags.length) {
    lastAnimationDelay = tagsAnimationDelay = lastAnimationDelay + 0.2;
  }

  let tasksAnimationDelay;
  if (taskInstances && taskInstances.length) {
    lastAnimationDelay = tasksAnimationDelay = lastAnimationDelay + 0.2;
  }

  let notesAnimationDelay;
  if (summary) {
    lastAnimationDelay = notesAnimationDelay = lastAnimationDelay + 0.2;
  }

  let todaysNotesAnimationDelay;
  if (notes && notes.length) {
    lastAnimationDelay = todaysNotesAnimationDelay = lastAnimationDelay + 0.2;
  }

  const timeClockNotesAnimationDelay = lastAnimationDelay + 0.2;
  const buttonsAnimationDelay = lastAnimationDelay + 0.2;

  return {
    timeAnimationDelay,
    locationAnimationDelay,
    positionAnimationDelay,
    tagsAnimationDelay,
    tasksAnimationDelay,
    notesAnimationDelay,
    todaysNotesAnimationDelay,
    timeClockNotesAnimationDelay,
    buttonsAnimationDelay
  };
}

SingleShiftFields.propTypes = {
  type: PropTypes.string,
  shift: PropTypes.object,
  signOut: PropTypes.func,
  startBreak: PropTypes.func,
  usTimeFormat: PropTypes.bool,
  deviceHeight: PropTypes.number
};
